import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SafeSvgPipe } from '@shared/pipes';
import * as SvgResources from '../lib/svg.resources';
import { FilterPanel } from '../types/filter-panel';
import * as FilterPanelConstants from '../types/constants';
import * as FilterPanelTypes from '../types/types';
import { IFacetedFilters, IFacetedFilterSelection } from '../types/faceted-filter.types';
import { FilterReturnEventType } from '../types/constants';
import { of } from 'rxjs';
import { skipWhile, take } from 'rxjs/operators';
import { compareSelections, FilterWellFacade } from './filter-well.facade';

@Component({
  selector: 'app-filter-well',
  templateUrl: './filter-well.component.html',
  styleUrls: ['./filter-well.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgStyle,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatTooltipModule,
    SafeSvgPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterWellComponent implements OnInit, OnDestroy {
  private readonly panelRef = inject(FilterPanel);
  private readonly facade = inject(FilterWellFacade);

  // Keep as property for template compatibility
  private _initializing = false;
  readonly initializingInput = input(false, { alias: 'initializing' });
  get initializing(): boolean {
    return this._initializing || this.initializingInput();
  }
  set initializing(value: boolean) {
    this._initializing = value;
  }

  readonly onFilterChange = output<IFacetedFilters>();
  readonly onFilterReset = output<IFacetedFilters>();
  readonly onToolbarInitialized = output<IFacetedFilters>();

  readonly mobileAnchorRef = viewChild<ElementRef>('mobileAnchor');
  get mobileAnchor(): ElementRef | undefined {
    return this.mobileAnchorRef();
  }

  svgIcons = SvgResources.svgIcons;
  svgIconTypes = SvgResources.svgIconTypes;
  filterIcon = SvgResources.sizableSvgIcon(this.svgIconTypes.filter, 6);
  addIcon = SvgResources.sizableSvgIcon(this.svgIconTypes.plus, 4);
  downIcon = SvgResources.sizableSvgIcon(this.svgIconTypes.chevronDown, 4);
  closeIcon = SvgResources.sizableSvgIcon(this.svgIconTypes.close, 4);

  selectedFilterItems: IFacetedFilterSelection[] = [];
  filters: IFacetedFilters = {} as IFacetedFilters;
  defaults: IFacetedFilterSelection[] = [];
  filterInputData: FilterPanelTypes.IPanelInputData = { inputData: [] };

  filtersDirty = false;
  mobilePanelOpen = false;
  mobileResultPanelOpen = false;
  noData = true;

  async ngOnInit(): Promise<void> {
    console.debug('FilterWellComponent OnInit');
    await this.facade.getData();

    this.facade.viewModel$
      .pipe(
        skipWhile((c) => !Object.keys(c).length),
        take(1)
      )
      .subscribe(({ filterData, defaultFilters, cachedFilters, urlFilters, config, noData }) => {
        console.debug('facade viewModel$ event filterData', filterData);
        if (!noData) {
          this.filters = {
            contextName: config.context,
            filters: [],
          };
          this.noData = false;
          this.filterInputData = { inputData: filterData.items };
          this.defaults = [...defaultFilters];
          const nonDisregardedDefaults = this.facade.getNondisregardedDefaults(this.defaults);
          this.selectedFilterItems = urlFilters.length ? urlFilters : [...nonDisregardedDefaults, ...cachedFilters];
          this.filtersDirty = cachedFilters?.length > 0 || this.defaults?.length !== nonDisregardedDefaults?.length;
          this.returnResult(FilterReturnEventType.Initializing);
        } else this.initWithNoData();
      });
  }

  ngOnDestroy(): void {
    this.facade.clearData();
  }

  filterItemSelected(item: IFacetedFilterSelection, chipElement: ElementRef | HTMLElement): void {
    this.showPanel(this.toElementRef(chipElement), item);
  }

  mobileFilterItemSelected(item: IFacetedFilterSelection, chipElement: ElementRef | HTMLElement | undefined): void {
    if (!chipElement) return;
    if (this.mobileResultPanelOpen) this.closeMobileResultPanel();

    this.mobilePanelOpen = true;
    this.showPanel(this.toElementRef(chipElement), item);
  }

  removeFilterItem(item: IFacetedFilterSelection): void {
    const idx = this.selectedFilterItems.findIndex((fi) => fi.path === item.path);
    this.selectedFilterItems.splice(idx, 1);

    this.filtersDirty = !(this.selectedFilterItems.length && this.defaults.length);

    if (this.mobileResultPanelOpen && !this.selectedFilterItems.length) this.closeMobileResultPanel();

    if (this.defaults.findIndex((d) => d.path === item.path) > -1) this.facade.setDefaultAsDisregarded(item);

    this.returnResult();
  }

  newFilterClicked(el: ElementRef | HTMLElement): void {
    if (this._initializing || this.noData) return;

    this.showPanel(this.toElementRef(el));
  }

  private toElementRef(element: ElementRef | HTMLElement): ElementRef {
    return element instanceof ElementRef ? element : new ElementRef(element);
  }

  resetFilters(): void {
    this.selectedFilterItems = [...this.defaults];
    this.returnResult(FilterReturnEventType.Resetting);
    this.filtersDirty = false;
    this.facade.clearState();

    if (this.mobileResultPanelOpen && !this.selectedFilterItems.length) this.closeMobileResultPanel();
  }

  showMobilePanel(): void {
    if (this.selectedFilterItems.length) {
      this.openMobileResultPanel();
    } else {
      this.openMobileFilterPanel();
    }
  }

  openMobileResultPanel(): void {
    this.mobileResultPanelOpen = true;
  }

  closeMobileResultPanel(): void {
    this.mobileResultPanelOpen = false;
  }

  openMobileFilterPanel(): void {
    this.closeMobileResultPanel();
    const anchor = this.mobileAnchor;
    if (anchor) {
      this.showPanel(anchor);
    }
    this.mobilePanelOpen = true;
  }

  closeMobileFilterPanel(): void {
    this.mobilePanelOpen = false;
  }

  private showPanel(origin: ElementRef, selectedItem?: IFacetedFilterSelection): void {
    const panelConfig: FilterPanelTypes.IFilterPanelConfig = {
      panelTitle: 'Filter',
      panelClass: 'lrgPanel',
      category: 'IsTheConfigRight',
      type: 'faceted_filter',
      target: 'panel',
      inputData: of(this.filterInputData),
      query: undefined,
      queryTarget: undefined,
      singleSelectionMode: false,
    };

    if (selectedItem) panelConfig.inputFacetData = selectedItem;

    const panelParams: FilterPanelTypes.FilterPanelParams = {
      originElement: origin,
      inputDataType: 'faceted_filter',
      config: panelConfig,
    };

    const panelRef = this.panelRef.open(panelParams);
    panelRef.afterClosed$.subscribe((result) => {
      if (result.dialogData.resultType === FilterPanelConstants.FilterPanelCloseType.applied) {
        const selected: IFacetedFilterSelection = <IFacetedFilterSelection>result.dialogData.data[0];
        if (selected) {
          if (selectedItem) this.updateSelection(selectedItem, selected);
          else this.addIfNotExist(selected);
        }
        if (this.mobilePanelOpen) this.closeMobileFilterPanel();
      }
    });
  }

  private updateSelection(currentItem: IFacetedFilterSelection, updatedItem: IFacetedFilterSelection): void {
    const currentIdx: number = this.selectedFilterItems.findIndex((sfi) => compareSelections(sfi, currentItem));

    if (currentIdx >= 0) {
      this.selectedFilterItems[currentIdx] = { ...updatedItem };
      this.filtersDirty = true;
    }

    if (!this.facade.isDisregardedDefault(currentItem)) this.facade.setDefaultAsDisregarded(currentItem);

    this.returnResult();
  }

  private addIfNotExist(newItem: IFacetedFilterSelection): void {
    // TODO: Need to add compare method for this
    const selection = this.selectedFilterItems.find((s) => compareSelections(s, newItem));

    if (!selection) {
      this.selectedFilterItems.push(newItem);
      this.filtersDirty = true;
      this.returnResult();
    } else console.error('Selection already exists');
  }

  private returnResult(eventType?: FilterReturnEventType): void {
    console.debug('FILTERWELL|returnResult|this.selectedFilterItems', this.selectedFilterItems);
    this.filters.filters = [
      ...this.selectedFilterItems.map((sfi) => sfi.payload).filter((p): p is NonNullable<typeof p> => p !== undefined),
    ];
    switch (eventType) {
      case FilterReturnEventType.Initializing:
        this.onToolbarInitialized.emit(this.filters);
        this._initializing = false;
        break;
      case FilterReturnEventType.Resetting:
        this.onFilterReset.emit(this.filters);
        break;
      default:
        this.onFilterChange.emit(this.filters);
    }

    this.facade.setState(this.selectedFilterItems);
  }

  private initWithNoData(): void {
    this.filters.filters = [];
    this.onToolbarInitialized.emit(this.filters);
  }
}
